import { Link, useLocation } from "react-router-dom";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import {
  FileText,
  CheckSquare,
  ShoppingCart,
  Settings,
  HardDrive,
  Trash2,
  User,
  Users,
  Code,
} from "lucide-react";

// Custom icon components with badges
const NotePersonIcon = ({ className }) => (
  <div className="relative">
    <FileText className={className} />
    <User className="w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 text-primary" />
  </div>
);

const NoteGroupIcon = ({ className }) => (
  <div className="relative">
    <FileText className={className} />
    <Users className="w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 text-primary" />
  </div>
);

const TodoPersonIcon = ({ className }) => (
  <div className="relative">
    <CheckSquare className={className} />
    <User className="w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 text-primary" />
  </div>
);

const TodoGroupIcon = ({ className }) => (
  <div className="relative">
    <CheckSquare className={className} />
    <Users className="w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 text-primary" />
  </div>
);

const navItems = [
  { key: "my-notes", label: "My Notes", icon: NotePersonIcon, path: "/" },
  { key: "common-notes", label: "Common Notes", icon: NoteGroupIcon, path: "/common-notes" },
  { key: "my-todos", label: "My To-Dos", icon: TodoPersonIcon, path: "/my-todos" },
  { key: "common-todos", label: "Common To-Dos", icon: TodoGroupIcon, path: "/common-todos" },
  { key: "orders", label: "Orders", icon: ShoppingCart, path: "/orders" },
  { key: "files", label: "File Manager", icon: HardDrive, path: "/files" },
  { key: "recycle-bin", label: "Recycle Bin", icon: Trash2, path: "/recycle-bin" },
];

const userItems = [
  { key: "api-docs", label: "API Documentation", icon: Code, path: "/api-docs" },
  { key: "settings", label: "User Settings", icon: User, path: "/settings" },
];

const adminItems = [
  { key: "admin", label: "Admin Panel", icon: Settings, path: "/admin" },
];

// Desktop Navigation (vertical sidebar) - ALWAYS visible, smaller on mobile
const DesktopNav = ({ activeModule, isAdmin }) => {
  const location = useLocation();

  return (
    <TooltipProvider delayDuration={0}>
      <nav className="w-10 md:w-14 border-r border-border bg-background/50 flex flex-col items-center py-2 md:py-3 gap-0.5 md:gap-1 shrink-0">
        {/* Main Navigation Items */}
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeModule === item.key || 
            (item.key === "my-notes" && location.pathname === "/");
          
          return (
            <Tooltip key={item.key}>
              <TooltipTrigger asChild>
                <Link to={item.path}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-8 w-8 md:h-10 md:w-10",
                      isActive && "bg-primary/10 text-primary"
                    )}
                    data-testid={`nav-${item.key}`}
                  >
                    <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">
                {item.label}
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Spacer */}
        <div className="flex-1" />

        {/* User Settings */}
        {userItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeModule === item.key;
          
          return (
            <Tooltip key={item.key}>
              <TooltipTrigger asChild>
                <Link to={item.path}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-8 w-8 md:h-10 md:w-10",
                      isActive && "bg-primary/10 text-primary"
                    )}
                    data-testid={`nav-${item.key}`}
                  >
                    <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">
                {item.label}
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Admin Items */}
        {isAdmin && (
          <>
            <div className="w-4 md:w-6 h-px bg-border my-1" />
            {adminItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeModule === item.key;
              
              return (
                <Tooltip key={item.key}>
                  <TooltipTrigger asChild>
                    <Link to={item.path}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-8 w-8 md:h-10 md:w-10",
                          isActive && "bg-primary/10 text-primary"
                        )}
                        data-testid={`nav-${item.key}`}
                      >
                        <Icon className="w-4 h-4 md:w-5 md:h-5" />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </>
        )}
      </nav>
    </TooltipProvider>
  );
};

export const MainNav = ({ activeModule, isAdmin }) => {
  // Always show desktop nav - it's responsive and works on all screen sizes
  return <DesktopNav activeModule={activeModule} isAdmin={isAdmin} />;
};
